
import { DragDropContext, Droppable, Draggable } from "@/components/kanban/KanbanBoard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  MoveHorizontal, 
  Edit, 
  ListChecks, 
  Calendar, 
  User, 
  Phone, 
  Mail, 
  Check, 
  Clock,
  Power,
  DollarSign,
  FileText,
  Building,
  Home,
  Plug,
  Hash
} from "lucide-react";
import { FluxoCard, KanbanColumn } from "@/components/solar/kanban/types";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";

interface KanbanBoardProps {
  columns: KanbanColumn[];
  onDragEnd: (result: any) => void;
  onEditClick: (card: FluxoCard) => void;
  onViewProjeto: (card: FluxoCard) => void;
  formatDate: (date: Date) => string;
}

export const KanbanBoard = ({
  columns,
  onDragEnd,
  onEditClick,
  onViewProjeto,
  formatDate
}: KanbanBoardProps) => {
  // State to track which card is being hovered
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  // State to track card being dragged
  const [isDragging, setIsDragging] = useState(false);
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);

  // Function to handle drag start
  const handleDragStart = useCallback((result: any) => {
    setIsDragging(true);
    setDraggedCardId(result.draggableId);
  }, []);

  // Function to get relative time
  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - new Date(date).getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Hoje";
    if (diffInDays === 1) return "Ontem";
    if (diffInDays < 7) return `${diffInDays} dias atrás`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} semanas atrás`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} meses atrás`;
    return `${Math.floor(diffInDays / 365)} anos atrás`;
  };

  // Get priority color
  const getPriorityColor = (prioridade: string) => {
    switch(prioridade) {
      case "alta": return "border-red-500";
      case "media": return "border-yellow-500";
      case "baixa": return "border-green-500";
      default: return "border-gray-300";
    }
  };

  // Get priority badge variant
  const getPriorityBadge = (prioridade: string) => {
    switch(prioridade) {
      case "alta": return "destructive";
      case "media": return "warning";
      case "baixa": return "success";
      default: return "secondary";
    }
  };

  // Format currency
  const formatCurrency = (value?: number | string) => {
    if (!value) return "R$ 0,00";
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  // Handle drag end and reset states
  const handleDragEndComplete = (result: any) => {
    setIsDragging(false);
    setDraggedCardId(null);
    onDragEnd(result);
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEndComplete}>
      <ScrollArea className="h-[80vh] w-full" scrollHideDelay={0}>
        <div className="flex space-x-3 pb-4 px-1 pt-1" style={{ minWidth: columns.length * 280 + 'px' }}>
          {columns.map((column) => (
            <div key={column.id} className="flex-shrink-0 w-[270px]">
              <h3 className="font-medium mb-2 flex items-center px-2">
                {column.titulo}
                <span className="ml-2 text-xs bg-muted rounded-full px-2 py-1">
                  {column.cards.length}
                </span>
              </h3>
              
              <Droppable droppableId={column.id}>
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={cn(
                      "rounded-lg p-2 min-h-[550px] max-h-[75vh] overflow-y-auto transition-colors",
                      isDragging && "bg-muted/50 border-2 border-dashed border-primary/20",
                      "shadow-inner",
                      column.cor || "bg-muted/30"
                    )}
                    style={{
                      perspective: "1000px",
                      transition: "background-color 0.2s ease, border 0.2s ease"
                    }}
                  >
                    {column.cards.map((card, index) => (
                      <Draggable key={card.id} draggableId={card.id} index={index}>
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={cn(
                              "mb-3 shadow-sm hover:shadow-md transition-all transform-gpu",
                              snapshot.isDragging ? "border-primary shadow-md ring-2 ring-primary/20" : "",
                              `border-l-4 ${getPriorityColor(card.prioridade)}`,
                              card.cor ? card.cor : "bg-card",
                              "overflow-hidden"
                            )}
                            style={{
                              ...provided.draggableProps.style,
                              transform: card.perspective && (hoveredCardId === card.id || snapshot.isDragging)
                                ? "perspective(1000px) rotateY(5deg) scale(1.02)"
                                : "perspective(1000px) rotateY(0) scale(1)",
                              transition: snapshot.isDragging 
                                ? "transform 0.1s ease" 
                                : "all 0.3s ease",
                              boxShadow: (hoveredCardId === card.id || snapshot.isDragging)
                                ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                                : "",
                              opacity: draggedCardId === card.id ? 0.85 : 1
                            }}
                            onMouseEnter={() => setHoveredCardId(card.id)}
                            onMouseLeave={() => setHoveredCardId(null)}
                          >
                            <CardHeader className="p-3 pb-1">
                              <div className="flex justify-between items-start gap-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-1 flex-1">
                                  <MoveHorizontal className="h-3 w-3 text-muted-foreground mr-1 flex-shrink-0" />
                                  <span className="line-clamp-1">{card.titulo}</span>
                                </CardTitle>
                                <Badge variant={getPriorityBadge(card.prioridade)} className="text-[10px] h-5">
                                  {card.prioridade === "alta" ? "Alta" : card.prioridade === "media" ? "Média" : "Baixa"}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent className="p-3 pt-0">
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-1 mt-1">
                                  {card.potenciaKwp && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Power className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{card.potenciaKwp} kWp</span>
                                    </p>
                                  )}
                                  
                                  {card.integrador && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Building className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{card.integrador}</span>
                                    </p>
                                  )}
                                </div>
                                
                                {(card.valorVenda || card.valorKit) && (
                                  <div className="grid grid-cols-2 gap-1">
                                    {card.valorVenda && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <DollarSign className="h-3 w-3 flex-shrink-0" />
                                        <span className="truncate">Venda: {formatCurrency(card.valorVenda)}</span>
                                      </p>
                                    )}
                                    {card.valorKit && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <DollarSign className="h-3 w-3 flex-shrink-0" />
                                        <span className="truncate">Kit: {formatCurrency(card.valorKit)}</span>
                                      </p>
                                    )}
                                  </div>
                                )}
                                
                                {card.descricao && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex items-start gap-1">
                                    <FileText className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                    <span>{card.descricao}</span>
                                  </p>
                                )}
                                
                                <div className="grid grid-cols-2 gap-1 mt-1">
                                  {card.uc && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Home className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">UC: {card.uc}</span>
                                    </p>
                                  )}
                                  
                                  {card.concessionaria && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Plug className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{card.concessionaria}</span>
                                    </p>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-1">
                                  {card.numeroInstalacao && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Hash className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">Inst: {card.numeroInstalacao}</span>
                                    </p>
                                  )}
                                  
                                  {card.numeroCliente && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <User className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">Cliente: {card.numeroCliente}</span>
                                    </p>
                                  )}
                                </div>
                                
                                {card.checklistPreenchido && (
                                  <div className="mt-2 grid grid-cols-3 gap-1">
                                    <Badge 
                                      variant={card.checklistPreenchido.integrador ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.integrador ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.integrador && <Check className="h-2 w-2" />}
                                      <span className="truncate">Int</span>
                                    </Badge>
                                    <Badge 
                                      variant={card.checklistPreenchido.unidadeConsumidora ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.unidadeConsumidora ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.unidadeConsumidora && <Check className="h-2 w-2" />}
                                      <span className="truncate">UC</span>
                                    </Badge>
                                    <Badge 
                                      variant={card.checklistPreenchido.eletricos ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.eletricos ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.eletricos && <Check className="h-2 w-2" />}
                                      <span className="truncate">Elét</span>
                                    </Badge>
                                    <Badge 
                                      variant={card.checklistPreenchido.mecanicos ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.mecanicos ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.mecanicos && <Check className="h-2 w-2" />}
                                      <span className="truncate">Mec</span>
                                    </Badge>
                                    <Badge 
                                      variant={card.checklistPreenchido.financeiros ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.financeiros ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.financeiros && <Check className="h-2 w-2" />}
                                      <span className="truncate">Fin</span>
                                    </Badge>
                                    <Badge 
                                      variant={card.checklistPreenchido.documentos ? "outline" : "secondary"} 
                                      className={cn(
                                        "text-xs flex items-center gap-1 px-1",
                                        card.checklistPreenchido.documentos ? "border-primary/50" : ""
                                      )}
                                    >
                                      {card.checklistPreenchido.documentos && <Check className="h-2 w-2" />}
                                      <span className="truncate">Doc</span>
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                            <CardFooter className="p-3 pt-1 flex justify-between items-center border-t">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span title={formatDate(card.criadoEm)}>{getRelativeTime(card.criadoEm)}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 rounded-full hover:bg-primary/10"
                                  onClick={() => onEditClick(card)}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 rounded-full hover:bg-primary/10"
                                  onClick={() => onViewProjeto(card)}
                                >
                                  <ListChecks className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </CardFooter>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {column.cards.length === 0 && (
                      <div className="flex items-center justify-center h-20 border border-dashed rounded-lg border-muted-foreground/20 text-muted-foreground text-sm">
                        Sem cards
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </ScrollArea>
    </DragDropContext>
  );
};
